import type { Scene } from '@babylonjs/core/scene';

import type { WhiteboardLayout } from '../core/WhiteboardLayout';
import { ConnectionDrawer } from '../input/ConnectionDrawer';
import { ControlHintsController } from '../input/ControlHintsController';
import type { Grabber, GrabberHub } from '../input/Grabbers';
import type { ModeRegistry } from '../input/ModeDefinition';
import { buildModeDefinitions } from '../input/ModeDefinitions';
import { PaletteUI, STEP_ENGAGE, STEP_RELEASE, type TrayContext } from '../input/PaletteUI';
import type { RoomHub } from '../net/roomHub';
import type { TransformStreamer } from '../net/TransformStreamer';
import { DrawingManager } from '../possession/DrawingManager';
import { ModeRingRenderer } from '../possession/ModeRingRenderer';
import { PlacementManager } from '../possession/PlacementManager';
import { PossessionManager } from '../possession/PossessionManager';
import { ScanTool } from '../possession/ScanTool';
import { SnapIndicator } from '../possession/SnapIndicator';
import type { LobbyInput } from '../input/LobbyInput';
import { ToolMode } from '../possession/ToolMode';
import { ToolPropRenderer } from '../possession/ToolPropRenderer';
import { TrashCanRenderer } from '../possession/TrashCanRenderer';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';

/**
 * Owns the authoring gestures and decides which one is allowed to act.
 *
 * Arbitration is the reason this exists. Placing, grabbing and connecting all begin with the
 * dominant trigger closing now (plan/mode-hierarchy.md, "Grabbing moves from the dominant grip
 * to the dominant trigger; near/far is gone" — settled 2026-08-10; the trigger used to be only
 * the far/connect signal, with the grip a separate near/grab one), so without one place
 * deciding, taking a shape off the tray could also grab whatever happened to be behind it. The
 * rules are: carrying beats everything, holding beats drawing, and the tray is put away while
 * a carry is in progress.
 *
 * Holding a node now force-closes the tray, the same way carrying does — reversed from an
 * earlier design where holding was deliberately the one thing that did *not* close it, because
 * a held node's actions (edit text, duplicate, delete) used to live in the tray and closing it
 * would have made them unreachable. They no longer do: delete moved to a release gesture on the
 * off-hand trash can, duplicate has no replacement, and edit text survives only as the desktop
 * `e` key. With nothing left in that context to offer, `palette.openSuppressed` below (wired to
 * `possession.heldNodeId !== null`) stops the tray opening at all while a node is held, and
 * `arbitrate` force-closes one that was already open the instant a grab lands — see CLAUDE.md's
 * "Holding a node now force-closes the tray" entry for the fuller account of why the old rule
 * became obsolete rather than wrong.
 */
// spec: SPEC-077
export class AuthoringController {
  readonly palette: PaletteUI;
  readonly placement: PlacementManager;
  readonly possession: PossessionManager;
  readonly connections: ConnectionDrawer;
  readonly toolMode: ToolMode;
  readonly drawing: DrawingManager;
  readonly scan: ScanTool;
  readonly toolProp: ToolPropRenderer;
  readonly trashCan: TrashCanRenderer;
  readonly snapIndicator: SnapIndicator;
  readonly controlHints: ControlHintsController;
  readonly modeRing: ModeRingRenderer;

  /** The mode hierarchy's own single source of truth (plan/mode-hierarchy.md,
   *  `input/ModeDefinition.ts`) — built once, right after `toolMode` and `scan` both exist
   *  (see this class's own constructor). Everything below that used to decide "what does this
   *  mode do" by reading `toolMode.mode` through a chain of `if`s reads this instead. */
  readonly modes: ModeRegistry;

  private readonly disposers: (() => void)[] = [];

  /** Re-armed once *both* dominant stick axes fall back under STEP_RELEASE — the mode wheel's
   *  own copy of the identical hysteresis PaletteUI's off-hand stick already uses
   *  (STEP_ENGAGE/STEP_RELEASE, exported from there rather than redefined here), so a flick
   *  that cycles the tool or steps the marker width feels the same as a flick that steps the
   *  tray. Shared by both axes deliberately, not one flag per axis — arming and disarming
   *  together is what makes a diagonal push resolve to exactly one step (see
   *  `tickToolCycle`'s own doc) rather than two independent flicks each free to fire on their
   *  own schedule. */
  private dominantStickArmed = true;

  /** Surfaced to the user; the caller decides how. */
  onNotice: ((message: string) => void) | null = null;

  constructor(
    // Not `private readonly` — this class only ever needs `scene` locally, to construct the
    // sibling classes below; nothing here reads `this.scene` back, unlike `grabbers`/`state`/
    // `hub`/`view`, which genuinely are read elsewhere in the class.
    scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,
    streamer: TransformStreamer,
    context: { orgId: string; roomId: string; diagramId: string | null },
    whiteboard: WhiteboardLayout | null = null,
  ) {
    this.palette = new PaletteUI(scene, grabbers);

    // Wired before anything can call palette.setCatalog (main.ts does, right after
    // constructing this controller) — setCatalog reads modelTemplateFor eagerly while it
    // builds each category's minis, unlike the palette's other caller-supplied hooks, which are
    // only ever read per-tick and so tolerate being assigned later.
    this.palette.modelTemplateFor = (key) => view.modelTemplateFor(key);

    this.placement = new PlacementManager(scene, grabbers, state, view, hub, context);

    // Built before PossessionManager and ConnectionDrawer, which read it directly rather than
    // through a callback (see PossessionManager's own constructor comment) — construction
    // order has to put it first for that to be possible at all. Pure state, unlike the
    // PenLedge it replaces: no Scene dependency, nothing to `.start()`.
    this.toolMode = new ToolMode(whiteboard);

    // Reads `toolMode` directly (its own trigger gesture only runs in Link mode — see that
    // class's own module doc). PossessionManager no longer needs a reference to this instance
    // at all: the two used to have to arbitrate a shared trigger press within one (build) mode,
    // which needed PossessionManager to ask ConnectionDrawer directly whether a grabber was
    // mid-drag; now that grabbing and linking are confined to disjoint modes, `toolMode` alone
    // already tells each class whether its own gesture applies this frame.
    this.connections = new ConnectionDrawer(scene, grabbers, state, view, hub, this.toolMode);

    this.possession = new PossessionManager(
      scene,
      grabbers,
      state,
      view,
      hub,
      streamer,
      this.placement,
      this.toolMode,
    );

    this.drawing = new DrawingManager(
      scene,
      grabbers,
      state,
      view,
      hub,
      streamer,
      this.toolMode,
      whiteboard,
    );

    // The scan tool's own gesture — see that class's own doc. Built alongside `drawing`
    // rather than folded into it: the two tools' state machines (touch/remote draw vs.
    // press-sweep-release range selection) share nothing but `toolMode` and the board's own
    // aim-ray helper (`remoteBoardPoint`), and keeping them separate is what let the scan
    // tool's own transcribe-and-replace pipeline stay entirely inside one class.
    this.scan = new ScanTool(scene, grabbers, state, view, hub, this.toolMode, whiteboard, context);

    // The mode hierarchy's own single source of truth (plan/mode-hierarchy.md) — built now that
    // both `toolMode` and `scan` exist, since Draw's and Scan's own axis bindings close over
    // them. See `input/ModeDefinitions.ts`'s own doc for why this is a function rather than
    // static data the way `NodeCatalog` is.
    this.modes = buildModeDefinitions({ toolMode: this.toolMode, scan: this.scan });

    // Renders whichever pen or the eraser toolMode currently selects, in the dominant hand,
    // for as long as the mode is draw — restoring "the mode is what is in your dominant hand"
    // (plan/history/control-modes.md, M12B) now that the writing point is visible again, not merely
    // computed. Reads the same toolMode/grabbers this.drawing does; nothing here feeds back
    // into either — DrawingManager.contactPointFor stays the single source of truth for where
    // the tip is, and this class only ever reads its result, never the reverse. Which prop, if
    // any, a mode holds is that mode's own to say (`this.modes[mode].heldProp`) — the explicit
    // `undefined` keeps `ToolPropRenderer`'s own default `ModelLibrary`.
    this.toolProp = new ToolPropRenderer(
      scene,
      grabbers,
      this.toolMode,
      undefined,
      (mode) => this.modes[mode].heldProp,
    );

    // "Dominant grip: mode ring appears" (plan/mode-hierarchy.md) — shown for as long as the
    // dominant grip is held, listing every mode with the current one highlighted. Reads
    // toolMode/grabbers directly, the same sibling-read shape every other per-frame renderer in
    // this constructor already uses.
    this.modeRing = new ModeRingRenderer(scene, grabbers, this.toolMode, this.modes);

    // The off hand's own trash can (plan/ui-improvements.md, item 3), shown for as long as
    // `this.possession.heldNodeId` is set — a sibling read directly, the same shape `toolProp`
    // reads `toolMode` with, not a callback. Purely a rendered affordance: the release-deletes
    // decision itself lives entirely in `PossessionManager.release`, which this class never
    // touches — see `TrashCanRenderer`'s own doc for why the two stay independent.
    this.trashCan = new TrashCanRenderer(scene, grabbers, this.possession);

    // Reads placement/possession the same way ToolPropRenderer reads toolMode — a sibling read
    // directly, not a callback (see PossessionManager's own constructor comment on why). Built
    // last of the three: it only ever reads what the other two write, never the reverse.
    this.snapIndicator = new SnapIndicator(scene, view.roomOrigin, state, view, this.placement, this.possession);

    // "Show what the controls do, when you look at your controller" (CLAUDE.md task) — reads
    // toolMode.mode fresh every tick the same way toolProp already does, and reads
    // ControlBindings, the derived-not-described source of truth for the words it shows.
    this.controlHints = new ControlHintsController(scene, grabbers, this.toolMode);

    // The off-hand surface comes from the mode (plan/mode-hierarchy.md) — `this.offHandContext`
    // is the one place that reads `ModeDefinition.offHandSurface`, so a mode's own tray content
    // cannot leak into a different mode by construction: `readout` modes have no palette to
    // leak, and `place` is Build's own, gated on `offHandSurface === 'palette'`. See that
    // method's own doc for the full reasoning.
    this.palette.context = () => this.offHandContext();

    // The `readout` context's own content — whatever the current mode's stick axes are set to,
    // read fresh every tick (`ModeDefinition.readoutLines`). Empty, and so invisible, for
    // Build's own `place` context, which never reads this at all.
    this.palette.readoutLines = () => this.modes[this.toolMode.mode].readoutLines();

    // The `readout` context's own colour swatches — empty for every mode but Draw with a marker
    // selected (`ModeDefinition.readoutSwatches`, plan/mode-hierarchy.md's own draw-variants
    // task). Read fresh every tick, the same shape as `readoutLines` just above.
    this.palette.readoutSwatches = () => this.modes[this.toolMode.mode].readoutSwatches();

    // The `readout` context's own labelled option carousels — empty for every mode but Link,
    // whose line-style/arrow-direction axes this is the visual counterpart to (`readoutLines`,
    // above, already shows the same two values as text). Read fresh every tick, the same shape
    // as `readoutLines`/`readoutSwatches` just above.
    this.palette.readoutOptionRows = () => this.modes[this.toolMode.mode].readoutOptionRows();

    // The tray has nothing left to offer while a node is held — the context that used to show
    // there (`edit text · duplicate · delete`) is gone, replaced by the off hand's own trash can
    // (`this.trashCan`, plan/ui-improvements.md, item 3: "we don't need an explicit edit text
    // action or really duplicate on the left hand when grabbing a node"). Stopping the tray from
    // opening at all — rather than opening it onto an empty/irrelevant context — is also what
    // frees the off-hand stick for ordinary travel while holding: `XrManager`'s own
    // `travelSuppressed` reads `palette.isOpen` (main.ts), and a menu that never opens has
    // nothing left to suppress locomotion for. That is a deliberate, correct consequence of this
    // change, not a regression — the suppression existed only because gripping the off hand used
    // to repurpose its stick for navigating the (now-deleted) action row; with no row to
    // navigate, the stick has nothing competing for it any more.
    this.palette.openSuppressed = () => this.possession.heldNodeId !== null;

    this.connections.onEdgeAimed = (connectionId) => this.selectEdge(connectionId);

    this.palette.onPicked = (selection, grabber) => {
      // Taking from the tray must not also start an edge from whatever is behind it.
      this.connections.cancel();
      this.placement.begin(selection, grabber);
    };

    this.possession.onGrabRefused = (_nodeId, holderColor) => {
      // Named if we can, because "Ada is holding that" tells you to ask her, where "someone
      // else" leaves you waiting.
      const holder = [...state.peers.values()].find((peer) => peer.colorHex === holderColor);

      this.onNotice?.(
        holder ? `${holder.displayName} is holding that` : 'someone else is holding that',
      );
    };

    this.connections.onRefused = (reason) => this.onNotice?.(reason);
    this.drawing.onNotice = (message) => this.onNotice?.(message);
    this.scan.onNotice = (message) => this.onNotice?.(message);

    // Grid snapping's own honest-failure notice (plan/grid.md, "One thing still to decide") —
    // fires when the bounded nearest-free-cell search on placement or release finds nothing.
    this.placement.onNotice = (message) => this.onNotice?.(message);
    this.possession.onNotice = (message) => this.onNotice?.(message);
  }

  /**
   * Registers the per-frame work.
   *
   * Order is load-bearing: Babylon runs observers in registration order, so arbitration is
   * registered first and therefore runs before any gesture reads the frame. Grabbers must
   * already have been started by the caller, so they refresh ahead of all of this.
   */
  /**
   * The lobby's own gesture handling, when there is one. Assigned after construction rather than
   * taken as a constructor argument because the lobby is built alongside the room view, well
   * before this controller, and threading it through the constructor would mean every test that
   * builds an AuthoringController had to know about a surface most of them never touch.
   */
  lobbyInput: LobbyInput | null = null;

  start(scene: Scene): void {
    const arbiter = scene.onBeforeRenderObservable.add(() => this.arbitrate());
    this.disposers.push(() => scene.onBeforeRenderObservable.remove(arbiter));

    this.disposers.push(this.palette.start());
    this.disposers.push(this.placement.start());
    this.disposers.push(this.possession.start());
    this.disposers.push(this.connections.start());

    // Registered right after placement/possession so it reads this same frame's write to
    // whichever mesh it is carrying/holding is currently tracking, not last frame's — see
    // SnapIndicator.tick's own comment on why the field it reads needs registration order
    // rather than a forced computeWorldMatrix to stay fresh.
    this.disposers.push(this.snapIndicator.start());

    // DrawingManager reads toolMode.selection every frame; ToolMode itself needs no `.start()`
    // — it is pure state, nothing to tick.
    this.disposers.push(this.drawing.start());

    // Same shape as drawing.start() just above: reads toolMode.mode fresh every tick, nothing
    // about registration order relative to it matters since the two tools are mutually
    // exclusive by construction (ToolMode.mode is exactly one value at a time).
    this.disposers.push(this.scan.start());

    // Registered after palette.start() so a wheel spin that changes toolMode.selection this
    // same frame is already reflected by the time this reads it — and after drawing.start()
    // for no reason that matters: both read the same resolved toolMode/grabbers state fresh
    // every tick and neither feeds the other, so their relative order is a coin flip that
    // happens to land here.
    this.disposers.push(this.toolProp.start());

    // Order relative to the one above does not matter: this reads toolMode.mode and the
    // grabbers fresh every tick, the same shape as toolProp, and neither feeds the other.
    this.disposers.push(this.controlHints.start());

    // Reads possession.heldNodeId fresh every tick, the same shape as toolProp/controlHints
    // above — order relative to them does not matter for the identical reason.
    this.disposers.push(this.trashCan.start());

    // Reads toolMode.mode and the dominant grip fresh every tick, the same shape as the renderers
    // above — order relative to them does not matter for the identical reason.
    this.disposers.push(this.modeRing.start());
  }

  /**
   * Decides which gesture owns the frame. Carrying beats holding beats drawing.
   *
   * The dominant stick's flick is read here too, first, because this is the one place gestures
   * are arbitrated (CLAUDE.md) and a mode switch is exactly that: which of the two things the
   * dominant grip and trigger mean for every gesture below it in this same frame.
   */
  private arbitrate(): void {
    const dominant = this.grabbers.byRole('dominant');

    // THE LOBBY GOES FIRST, AND WHEN IT IS LISTENING NOTHING ELSE IS.
    //
    // A room showing no diagram has nothing to author (SPEC-161): no nodes to grab, no links to
    // draw, nothing to place. So this is not a priority ordering the way "carrying beats holding
    // beats drawing" is — it is a mode, in the same sense the rest of the control scheme uses the
    // word (plan/mode-hierarchy.md), and it is what keeps a trigger press from ever meaning both
    // "open this diagram" and "grab that node". LobbyInput checks the same fact for itself, so a
    // caller that forgets this is safe rather than merely lucky.
    if (this.lobbyInput?.isActive === true) {
      if (dominant !== null && dominant !== undefined) {
        this.lobbyInput.tick(dominant);
      }

      return;
    }

    this.tickToolCycle(dominant);

    if (this.placement.isCarrying) {
      this.connections.cancel();

      if (this.palette.isOpen) {
        this.palette.close();
      }

      return;
    }

    // Link mode owns the dominant trigger for its own drag-to-connect gesture
    // (`ConnectionDrawer.tick`, gated on `toolMode.isLinkMode`) — everywhere else, a connection
    // in progress does not belong and is cancelled unconditionally the instant the mode is
    // anything but link, the same way carrying above always cancels one regardless of whether
    // one was actually in progress. This also clears a link-in-progress the instant the
    // mode-switch flick above moves *away* from link mid-drag, in the same frame it happens,
    // since `tickToolCycle` already ran before this check this same call.
    if (!this.toolMode.isLinkMode) {
      this.connections.cancel();
    }

    if (this.toolMode.isToolActive) {
      // Draw and scan both reserve the trigger entirely for their own gesture
      // (plan/history/control-modes.md; ToolMode.isToolActive's own doc) — nothing further to arbitrate
      // for either. Link mode is also `isToolActive` (mode !== 'build') but needs no extra
      // handling here: its own trigger gesture is `connections.tick()`, ungated by anything in
      // this method, and its tray shows a readout rather than the shape palette, so there is
      // nothing to force-close the way carrying does. The tray itself is left alone in every
      // non-build mode — it is not force-closed, because it is always showing the readout.
      return;
    }

    // Holding no longer keeps the tray up (plan/ui-improvements.md, item 2 — the off hand is a
    // trash can instead, `this.trashCan`), so this force-closes it the same way carrying does
    // above, rather than waiting a tick for `PaletteUI`'s own `openSuppressed` check to notice.
    if (this.possession.heldNodeId !== null && this.palette.isOpen) {
      this.palette.close();
    }
  }

  /**
   * Reads the dominant thumbstick and dispatches to one of two entirely different things,
   * decided by whether the dominant grip is held — plan/mode-hierarchy.md's own central split:
   *
   * - **Grip held: the mode-switch gesture.** Left/right cycles the mode (`this.cycleTool`, the
   *   identical wrapper the desktop `p` key already used) — the one input every mode agrees on,
   *   unconditionally, replacing the old bare-stick tool flick entirely. Up/down is left unbound
   *   while the grip is held; nothing in the settled bindings table spends it.
   * - **Grip free: whichever axis binding the *current mode's own definition* supplies**
   *   (`this.modes[this.toolMode.mode].stickX`/`.stickY`, `input/ModeDefinition.ts`) — colour in
   *   Draw, marker width in Draw's other axis, scan line length in Scan, style/arrow in Link,
   *   nothing at all in Build (the owner's own instruction: "Build mode will not use the
   *   dominant stick"). A `null` binding does nothing and shows nothing; a bound one returns
   *   either a label to flash or `null` for "bound, but this press didn't actually change
   *   anything" (mirrors `cycleTool`'s own held-node guard, which can likewise refuse a change).
   *
   * This inverts the pre-redesign scheme, worth stating plainly because it reads backwards
   * against the git history otherwise: the bare stick used to switch tools and grip+stick used
   * to (only in draw mode) cycle colour. Now grip+stick always switches modes, and the bare
   * stick is what modes get to spend on their own axes — plan/mode-hierarchy.md's own bindings
   * table, "Dominant grip + stick left/right: switch mode" opposite "Dominant stick left/right
   * (no grip): [per-mode]" is the settled shape this method implements.
   *
   * One arm/release flag (`dominantStickArmed`) covers both regimes, the same
   * STEP_ENGAGE/STEP_RELEASE discipline the off-hand stick already uses for tray navigation —
   * and **the bigger axis wins a diagonal push**, the identical rule `PaletteUI.stepCursor`
   * already uses, so a flick that lands slightly off-axis reads as exactly one clean step. While
   * the grip is held, only X does anything — a bigger-magnitude Y still consumes the flick (the
   * stick must return toward centre before the next one counts) but changes nothing, the same
   * "armed but inert" shape a Y flick in Build mode already has when the grip is free.
   *
   * Used to also flash a floating "which mode"/"which value" label here (`ToolSwitchLabel`) —
   * deleted (plan/ui-improvements.md, the owner's own call: "we can also remove the floating
   * mode label, as that is now redundant"). The mode ring already shows the current mode by
   * construction (`ModeRingRenderer`, a `Carousel` whose front item always reads as "the one"),
   * and the dominant-hand readout already shows whatever a mode's own stick axis is currently
   * set to (`PaletteUI.readoutLines`/`readoutSwatches`) — a separate label restating either was
   * noise,
   * not a second source of information. `AxisBinding.step`'s own returned label is still called
   * for its mutation side effect below; nothing reads the string it returns any more.
   */
  private tickToolCycle(dominant: Grabber | undefined): void {
    const stick = dominant?.stick;

    if (!stick) {
      this.dominantStickArmed = true;
      return;
    }

    const magnitudeX = Math.abs(stick.x);
    const magnitudeY = Math.abs(stick.y);

    if (magnitudeX < STEP_RELEASE && magnitudeY < STEP_RELEASE) {
      this.dominantStickArmed = true;
    }

    if (!this.dominantStickArmed || (magnitudeX < STEP_ENGAGE && magnitudeY < STEP_ENGAGE)) {
      return;
    }

    this.dominantStickArmed = false;

    if (dominant?.near.pressed === true) {
      // The mode-switch gesture. Y is unbound while the grip is held — see this method's own
      // doc — so only an X-dominant push does anything at all.
      if (magnitudeX < STEP_ENGAGE || magnitudeX < magnitudeY) {
        return;
      }

      this.cycleTool(stick.x > 0 ? 1 : -1);
      return;
    }

    // Grip free: whichever axis the current mode's own definition binds.
    const definition = this.modes[this.toolMode.mode];
    let changed: string | null;

    if (magnitudeX >= magnitudeY) {
      changed = definition.stickX?.step(stick.x > 0 ? 1 : -1) ?? null;
    } else {
      // WebXR's thumbstick Y axis is negative-forward (PaletteUI.stepCursor's own comment:
      // pushing away from you, "up", is negative) — read that way here too, so pushing up
      // feels like "more" (thicker ink, a taller scan line) the same way pushing up on the
      // off-hand stick means "up" in the tray grid.
      changed = definition.stickY?.step(stick.y < 0 ? 1 : -1) ?? null;
    }

    // Nudges the readout open (`PaletteUI.flashReadout`'s own doc has the full reasoning) —
    // every mode whose own axis just moved, not Link alone any more. This used to be Link-only:
    // Draw and Scan already put something visible in the dominant hand (`ModeDefinition
    // .heldProp` — the marker/eraser prop, the scanner — rendered by `ToolPropRenderer`), while
    // Link's own `heldProp` is `'none'`, so Link was the one mode that needed a flash to show
    // anything happened at all. That reasoning stopped mattering once the readout itself moved
    // onto the dominant hand (plan/mode-hierarchy.md, "A readout belongs on the hand that
    // chooses it"): a flash is now the *only* way any of the three ever opens (`PaletteUI
    // .updateReadoutVisibility` has no grip-hold path left to fall back to), so Draw and Scan
    // need it exactly as much as Link does, not merely as a nice-to-have. Build's own axes are
    // unbound (`ModeDefinition.stickX`/`stickY` are undefined there), so `changed` is always
    // `null` for it and this never fires for Build regardless.
    if (changed !== null) {
      this.palette.flashReadout();
    }
  }

  /**
   * Cycles the dominant hand's tool — the mode wheel's own action (plan/history/control-modes.md), and
   * the desktop's `p` key's (DevSim.ts), and the tray's "pen" item's. A no-op with no dominant
   * grabber currently present.
   *
   * Guarded against `possession.heldNodeId` only on the transition *out of* build mode:
   * leaving build is the only direction that can put a node and a tool in the same hand at
   * once (the reverse guard in `PossessionManager.considerGrab` already keeps a *grab* from
   * starting while any non-build tool is active, so returning to build is always safe) — an
   * adversarial review of the click this replaces proved that collision by driving it with a
   * node held and finding both a node and a pen attached to the same hand afterward. With
   * `build`/`draw`/`scan`, "leaving build" covers both destinations at once — cycling to
   * `draw` *or* to `scan` while holding a node is refused identically — so this guard is
   * checked once, not per-tool.
   */
  cycleTool(direction: 1 | -1 = 1): void {
    const grabber = this.grabbers.byRole('dominant');

    if (!grabber) {
      return;
    }

    if (this.toolMode.mode === 'build' && this.possession.heldNodeId !== null) {
      return;
    }

    this.toolMode.cycleTool(direction);

    // An edge's selection is Link mode's own concern (plan/mode-hierarchy.md, "Edge selection
    // and relabelling move into Link mode") — leaving it thickened stale would let a later mode
    // go on showing it selected, or let re-entering Link later reopen a selection nobody meant
    // to keep.
    if (!this.toolMode.isLinkMode) {
      this.selectEdge(null);
    }
  }

  /**
   * The off-hand surface comes from the mode (plan/mode-hierarchy.md) — the one place
   * `ModeDefinition.offHandSurface` is read, so `PaletteUI.context` never has to know about
   * modes itself. `'palette'` is Build's own interactive surface (the shape grid) and
   * `'readout'` is what every other mode's own definition returns, uniformly: a mode's own
   * definition decides *what* the readout shows (`readoutLines`, wired separately, above), not
   * *whether* one shows at all.
   *
   * Link used to be an exception here, swapping its own readout for an `edge` action row once
   * `this.connections.onEdgeAimed` selected one — that row offered a single action, `label`,
   * which opened the (now-deleted) in-VR note editor (plan/ui-improvements.md, item 4). With no
   * action left to show for a selected edge, Link's off-hand surface is `readout`
   * unconditionally now, same as every other non-Build mode; aiming at an edge still selects
   * it and still thickens it in the scene (`selectEdge`, `RoomView.selectConnection`) — that is
   * a plain visual affordance now, independent of what the tray happens to be showing.
   */
  private offHandContext(): TrayContext {
    return this.modes[this.toolMode.mode].offHandSurface === 'palette' ? 'place' : 'readout';
  }

  /** Marks an edge as selected, or clears it. Thickens the edge so the choice is visible — a
   *  plain scene affordance with no tray content behind it any more (see `offHandContext`'s own
   *  doc for why). `cycleTool` clears it on leaving Link, so it can never linger stale into a
   *  different mode. */
  private selectEdge(connectionId: string | null): void {
    this.view.selectConnection(connectionId);
  }

  /**
   * Sets the label of an edge, given its own hub round trip — `hub.updateConnection`, the same
   * capability `RoomHub` has always exposed.
   *
   * No caller left in the running app: the only in-VR entry point, Link mode's `label` tray
   * action, was removed alongside the in-VR note editor it opened
   * (plan/ui-improvements.md, item 4) — a node's own label is reached through speech-to-text
   * instead (`NodeLabelController`), but that path does not generalise to an edge (no lock
   * concept exists for a connection the way `Node`'s grab does, and the server-side transcription
   * pipeline is written against a node's own id and lock end to end — see that removal's own PR
   * for the full account). Kept, not deleted, because it is a small, self-contained, correctly
   * tested unit built directly on a real server capability, not itself a piece of the deleted UI —
   * `AuthoringController.test.ts` still drives it directly, with no grab or `Grabber` involved.
   * Returns whether it actually succeeded, rather than `Promise<void>`, so a future caller can
   * show its own "label saved" only when one is warranted — a failure already surfaces through
   * `onNotice` from inside here. Same shape as `setNodeText` below — see its own doc comment for
   * the fuller account of why.
   */
  async setEdgeLabel(connectionId: string, label: string): Promise<boolean> {
    try {
      this.state.upsertConnection(await this.hub.updateConnection({ connectionId, label }));
      return true;
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
      return false;
    }
  }

  /** Deletes the node currently held. Requires the lock, which holding it guarantees. Called
   *  from the desktop's Delete/Backspace key (DevSim.ts) — the off-hand trash can
   *  (`TrashCanRenderer`/`PossessionManager.release`) is the VR equivalent, and does not call
   *  through here: it has its own release-only path, since desktop has no off hand to drop a
   *  node into. */
  async deleteHeld(): Promise<void> {
    const nodeId = this.possession.heldNodeId;

    if (nodeId === null) {
      this.onNotice?.('grab something first to delete it');
      return;
    }

    try {
      await this.hub.deleteNode(nodeId);
      this.onNotice?.('deleted');
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Sets a node's text — a shape's label (CLAUDE.md, "Node.Text is a shape's label").
   *
   * No caller left in the running app: `NoteEditor`, the in-VR editor this was built for, is
   * gone (plan/ui-improvements.md, item 4) — a node's own label is reached through
   * speech-to-text instead (`NodeLabelController`), which applies a transcribed label directly
   * through the hub rather than through this method. Kept for the same reason `setEdgeLabel`
   * above is: a small, self-contained, correctly tested unit built directly on a real server
   * capability (`hub.updateNode`), not itself a piece of the deleted UI — and the bug history
   * below is worth keeping regardless of what currently calls it.
   *
   * Took `nodeId` explicitly, the id `NoteEditor` captured when its keyboard opened — this
   * used to be `setHeldText(text)`, reading `this.possession.heldNodeId` instead, on the
   * assumption that whatever opened the editor would still be held by the time it closed. It
   * will not be, reliably: main.ts opens the editor while the node is held and the tray's own
   * dwell has already run once to get here, but nothing about typing into NoteEditor's virtual
   * keyboard needs the grab that opened it to stay closed for the however-many seconds real
   * text entry takes, and the natural thing to do with the hand that was holding the node is
   * let go of it to poke keys instead. When that happened, `heldNodeId` was already null by
   * commit time, the old `if (nodeId === null) { return; }` guard silently did nothing, and
   * main.ts's `.then(() => showNotice('saved'))` fired anyway, regardless — a false "saved"
   * for an edit that never reached the server. This is the confirmed cause of
   * plan/open-items.md's "Adding text to a shape does not work".
   *
   * No local pre-check of `this.state.nodes` either, matching `setEdgeLabel` above: the server
   * is the single source of truth for both "does this node still exist" and "do you still hold
   * its lock" (`RequireLock` inside `UpdateNode` — CLAUDE.md, "Grab = lock = permission to
   * edit"). If the lock really was released, this throws and the catch below surfaces the
   * server's own message ("Grab node ... before editing it.") instead of a false success — an
   * honest failure the user can act on, not a silent no-op.
   *
   * Returns whether it succeeded, the same reason and the same fix as `setEdgeLabel`'s own
   * `Promise<boolean>`: main.ts's success notice must not fire on top of the real error this
   * already surfaced through `onNotice`.
   */
  async setNodeText(nodeId: string, text: string): Promise<boolean> {
    try {
      // Same convention as creating: the update is broadcast to others and returned to the
      // caller, so the person who typed the text is the one who has to apply it.
      this.state.upsertNode(await this.hub.updateNode({ nodeId, text }));
      return true;
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
      return false;
    }
  }

  dispose(): void {
    for (const dispose of this.disposers.splice(0)) {
      dispose();
    }

    this.palette.dispose();
    this.connections.dispose();
    this.drawing.dispose();
    this.scan.dispose();
    this.toolProp.dispose();
    this.trashCan.dispose();
    this.snapIndicator.dispose();
    this.controlHints.dispose();
    this.modeRing.dispose();
  }
}
